import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  TrendingDown, 
  RefreshCw, 
  ExternalLink,
  Clock,
  BarChart2,
  Activity
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useMarketData, COINGECKO_IDS, COIN_NAMES } from '@/context/MarketDataContext';

// Crypto icons as SVG components for better quality
const CryptoIcon = ({ symbol, size = 32 }) => {
  const icons = {
    BTC: (
      <svg viewBox="0 0 32 32" width={size} height={size}>
        <circle cx="16" cy="16" r="16" fill="#F7931A"/>
        <path fill="#FFF" d="M22.5 14.2c.3-2-1.2-3.1-3.3-3.8l.7-2.7-1.7-.4-.7 2.6c-.4-.1-.9-.2-1.4-.3l.7-2.7-1.7-.4-.7 2.7c-.4-.1-.7-.2-1-.2l-2.3-.6-.4 1.8s1.2.3 1.2.3c.7.2.8.6.8 1l-.8 3.1c0 0 .1 0 .2.1h-.2l-1.1 4.5c-.1.2-.3.5-.8.4 0 0-1.2-.3-1.2-.3l-.8 1.9 2.2.5c.4.1.8.2 1.2.3l-.7 2.8 1.7.4.7-2.7c.5.1.9.2 1.4.3l-.7 2.7 1.7.4.7-2.8c2.8.5 5 .3 5.9-2.2.7-2-.1-3.2-1.5-4 1.1-.2 1.9-1 2.1-2.5zm-3.8 5.3c-.5 2-4 .9-5.1.7l.9-3.7c1.1.3 4.7.8 4.2 3zm.5-5.4c-.5 1.8-3.3.9-4.3.7l.8-3.3c1 .2 4 .7 3.5 2.6z"/>
      </svg>
    ),
    ETH: (
      <svg viewBox="0 0 32 32" width={size} height={size}>
        <circle cx="16" cy="16" r="16" fill="#627EEA"/>
        <path fill="#FFF" fillOpacity=".6" d="M16 4v8.9l7.5 3.3z"/>
        <path fill="#FFF" d="M16 4L8.5 16.2l7.5-3.3z"/>
        <path fill="#FFF" fillOpacity=".6" d="M16 21.9v6.1l7.5-10.4z"/>
        <path fill="#FFF" d="M16 28v-6.1l-7.5-4.3z"/>
        <path fill="#FFF" fillOpacity=".2" d="M16 20.6l7.5-4.4-7.5-3.3z"/>
        <path fill="#FFF" fillOpacity=".6" d="M8.5 16.2l7.5 4.4v-7.7z"/>
      </svg>
    ),
    BNB: (
      <svg viewBox="0 0 32 32" width={size} height={size}>
        <circle cx="16" cy="16" r="16" fill="#F3BA2F"/>
        <path fill="#FFF" d="M12.1 14.5L16 10.6l3.9 3.9 2.3-2.3L16 6l-6.2 6.2 2.3 2.3zm-2.5 1.5L7.3 13.7 5 16l2.3 2.3 2.3-2.3zm2.5 1.5L16 21.4l3.9-3.9 2.3 2.3L16 26l-6.2-6.2 2.3-2.3zm9.8-1.5l2.3-2.3L26.5 16l-2.3 2.3-2.3-2.3zM19 16l-3-3-3 3 3 3 3-3z"/>
      </svg>
    ),
    SOL: (
      <svg viewBox="0 0 32 32" width={size} height={size}>
        <circle cx="16" cy="16" r="16" fill="url(#sol-gradient)"/>
        <defs>
          <linearGradient id="sol-gradient" x1="0" y1="0" x2="32" y2="32">
            <stop offset="0%" stopColor="#00FFA3"/>
            <stop offset="100%" stopColor="#DC1FFF"/>
          </linearGradient>
        </defs>
        <path fill="#FFF" d="M9.5 19.3l2.3-2.3h12.5l-2.3 2.3H9.5zm0-4.6h12.5l2.3-2.3H9.5l-2.3 2.3zm14.8 0l-2.3 2.3H9.5l2.3-2.3h12.5z"/>
      </svg>
    ),
    XRP: (
      <svg viewBox="0 0 32 32" width={size} height={size}>
        <circle cx="16" cy="16" r="16" fill="#23292F"/>
        <path fill="#FFF" d="M23.1 8h2.9l-5.3 5.1c-2.6 2.5-6.8 2.5-9.4 0L6 8h2.9l4.4 4.2c1.7 1.6 4.4 1.6 6.1 0L23.1 8zM8.9 24H6l5.3-5.1c2.6-2.5 6.8-2.5 9.4 0L26 24h-2.9l-4.4-4.2c-1.7-1.6-4.4-1.6-6.1 0L8.9 24z"/>
      </svg>
    ),
    ADA: (
      <svg viewBox="0 0 32 32" width={size} height={size}>
        <circle cx="16" cy="16" r="16" fill="#0033AD"/>
        <circle cx="16" cy="16" r="3" fill="#FFF"/>
        <circle cx="16" cy="8" r="1.5" fill="#FFF"/>
        <circle cx="16" cy="24" r="1.5" fill="#FFF"/>
        <circle cx="9" cy="12" r="1.5" fill="#FFF"/>
        <circle cx="23" cy="12" r="1.5" fill="#FFF"/>
        <circle cx="9" cy="20" r="1.5" fill="#FFF"/>
        <circle cx="23" cy="20" r="1.5" fill="#FFF"/>
      </svg>
    ),
    DOGE: (
      <svg viewBox="0 0 32 32" width={size} height={size}>
        <circle cx="16" cy="16" r="16" fill="#C2A633"/>
        <path fill="#FFF" d="M13 10h5c3.9 0 6 2.2 6 6s-2.1 6-6 6h-5V10zm3 9.5h1.5c2 0 3-1.1 3-3.5s-1-3.5-3-3.5H16v7z"/>
      </svg>
    ),
    USDT: (
      <svg viewBox="0 0 32 32" width={size} height={size}>
        <circle cx="16" cy="16" r="16" fill="#26A17B"/>
        <path fill="#FFF" d="M17.9 17.5v-.1c-.1 0-1.1-.1-2.9-.1s-2.7 0-2.9.1v.1c-3.4.2-6 .8-6 1.5s2.6 1.4 6 1.5v5h5.8v-5c3.4-.2 5.9-.8 5.9-1.5s-2.5-1.3-5.9-1.5zm-2.9 2.5c-4 0-6.5-.6-6.5-1.3s2.5-1.3 6.5-1.3 6.5.6 6.5 1.3-2.5 1.3-6.5 1.3zM22 10v3h-4v2h-4v-2H10v-3h4V8h4v2h4z"/>
      </svg>
    ),
  };
  
  return icons[symbol] || (
    <div 
      className="rounded-full bg-gray-500 flex items-center justify-center text-white font-bold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {symbol?.charAt(0)}
    </div>
  );
};

// Live Market Data Component - Now uses shared context
export function LiveMarketOverview() {
  const { priceData, loading, error, lastUpdated, fetchPriceData } = useMarketData();

  const formatPrice = (price) => {
    if (!price) return '$0.00';
    if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    if (price >= 1) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${price.toFixed(4)}`;
  };

  const formatChange = (change) => {
    if (!change && change !== 0) return '0.00%';
    return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
  };

  if (error && Object.keys(priceData).length === 0) {
    return (
      <div className="glass-card p-8 text-center">
        <Activity className="w-12 h-12 text-yellow-400/50 mx-auto mb-4" />
        <p className="text-white/60">Live market data temporarily unavailable</p>
        <button
          onClick={() => fetchPriceData(true)}
          className="mt-4 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg text-cyan-400 text-sm transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <h3 className="text-lg font-semibold text-white">Live Market</h3>
        </div>
        <div className="flex items-center gap-2 text-white/40 text-xs">
          {lastUpdated && (
            <>
              <Clock className="w-3 h-3" />
              <span>{lastUpdated.toLocaleTimeString()}</span>
            </>
          )}
          <button
            onClick={() => fetchPriceData(true)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Market Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.keys(COINGECKO_IDS).map((symbol) => {
          const data = priceData[symbol];
          const isPositive = data?.change24h >= 0;
          
          return (
            <motion.div
              key={symbol}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-3 hover:bg-white/5 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-2 mb-2">
                <CryptoIcon symbol={symbol} size={28} />
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-sm truncate">{symbol}</p>
                  <p className="text-white/40 text-xs truncate">{COIN_NAMES[symbol]}</p>
                </div>
              </div>
              
              {loading && !data ? (
                <div className="space-y-2">
                  <div className="h-5 bg-white/10 rounded animate-pulse" />
                  <div className="h-4 bg-white/5 rounded animate-pulse w-16" />
                </div>
              ) : (
                <>
                  <p className="text-white font-mono font-bold text-base mb-1">
                    {formatPrice(data?.price)}
                  </p>
                  <div className="flex items-center gap-1">
                    {isPositive ? (
                      <TrendingUp className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <TrendingDown className="w-3 h-3 text-hotpink-400" />
                    )}
                    <span className={`text-xs font-medium ${isPositive ? 'text-emerald-400' : 'text-hotpink-400'}`}>
                      {formatChange(data?.change24h)}
                    </span>
                  </div>
                </>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// TradingView-style Chart Component - Now uses shared context
export function TradingViewChart({ defaultSymbol = 'BTC' }) {
  const { fetchChartData } = useMarketData();
  const [selectedSymbol, setSelectedSymbol] = useState(defaultSymbol);
  const [timeframe, setTimeframe] = useState('7');
  const [chartData, setChartData] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(null);
  const [loading, setLoading] = useState(true);

  const timeframes = [
    { label: '24H', value: '1' },
    { label: '7D', value: '7' },
    { label: '1M', value: '30' },
    { label: '3M', value: '90' },
    { label: '1Y', value: '365' },
  ];

  const symbols = ['BTC', 'ETH', 'BNB', 'SOL', 'XRP', 'ADA', 'DOGE'];

  useEffect(() => {
    const loadChartData = async () => {
      setLoading(true);
      const result = await fetchChartData(selectedSymbol, timeframe);
      if (result) {
        setChartData(result.chartData);
        setCurrentPrice(result.currentPrice);
        setPriceChange(result.priceChange);
      }
      setLoading(false);
    };

    loadChartData();
  }, [selectedSymbol, timeframe, fetchChartData]);

  const formatPrice = (price) => {
    if (!price) return '$0.00';
    if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const isPositive = priceChange >= 0;
  const chartColor = isPositive ? '#00FF94' : '#FF006E';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-card p-2 text-sm border border-white/10">
          <p className="text-white/60 text-xs">{payload[0].payload.time}</p>
          <p className="text-white font-mono font-bold">{formatPrice(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="glass-card p-4 sm:p-6">
      {/* Chart Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        {/* Symbol Selector */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <CryptoIcon symbol={selectedSymbol} size={40} />
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-bold text-white">{selectedSymbol}/USD</h3>
                <select
                  value={selectedSymbol}
                  onChange={(e) => setSelectedSymbol(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                >
                  {symbols.map((s) => (
                    <option key={s} value={s} className="bg-[#0a0f1a]">{s}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 mt-1">
                {loading ? (
                  <div className="h-6 w-32 bg-white/10 rounded animate-pulse" />
                ) : (
                  <>
                    <span className="text-2xl font-mono font-bold text-white">
                      {formatPrice(currentPrice)}
                    </span>
                    <span className={`flex items-center gap-1 text-sm font-medium px-2 py-0.5 rounded ${
                      isPositive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-hotpink-400/20 text-hotpink-400'
                    }`}>
                      {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {priceChange ? `${isPositive ? '+' : ''}${priceChange.toFixed(2)}%` : '0.00%'}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Timeframe Selector */}
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
          {timeframes.map((tf) => (
            <button
              key={tf.value}
              onClick={() => setTimeframe(tf.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                timeframe === tf.value
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="h-64 sm:h-80">
        {loading ? (
          <div className="h-full w-full bg-white/5 rounded-lg animate-pulse flex items-center justify-center">
            <BarChart2 className="w-8 h-8 text-white/20" />
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColor} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={chartColor} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="time" 
                stroke="rgba(255,255,255,0.2)"
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis 
                stroke="rgba(255,255,255,0.2)"
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => formatPrice(value)}
                domain={['dataMin', 'dataMax']}
                width={70}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="price" 
                stroke={chartColor}
                strokeWidth={2}
                fill="url(#chartGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-white/40">
            No chart data available
          </div>
        )}
      </div>

      {/* Chart Footer */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
        <p className="text-white/40 text-xs">Data from CoinGecko</p>
        <a
          href={`https://www.coingecko.com/en/coins/${COINGECKO_IDS[selectedSymbol]}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-cyan-400 text-xs hover:underline"
        >
          View on CoinGecko
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

// Live Ticker Component - Now uses shared context
export function LiveTicker() {
  const { priceData, loading } = useMarketData();

  const formatPrice = (price) => {
    if (!price) return '$0';
    if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    return `$${price.toFixed(2)}`;
  };

  const tickerItems = Object.entries(priceData);
  
  if (loading && tickerItems.length === 0) {
    return (
      <div className="h-10 bg-[#0a0f1a]/80 flex items-center justify-center border-y border-white/10">
        <div className="flex items-center gap-2 text-white/30 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading market data...
        </div>
      </div>
    );
  }

  if (tickerItems.length === 0) {
    return null;
  }

  // Duplicate items for seamless loop
  const duplicatedItems = [...tickerItems, ...tickerItems];

  return (
    <div className="bg-[#0a0f1a]/80 border-y border-white/10 overflow-hidden">
      <div className="flex animate-ticker whitespace-nowrap py-2">
        {duplicatedItems.map(([symbol, data], index) => {
          const isPositive = data.change24h >= 0;
          return (
            <div 
              key={`${symbol}-${index}`}
              className="flex items-center gap-3 px-6 border-r border-white/10"
            >
              <CryptoIcon symbol={symbol} size={20} />
              <span className="text-white font-medium text-sm">{symbol}</span>
              <span className="text-white/80 font-mono text-sm">{formatPrice(data.price)}</span>
              <span className={`text-xs font-medium ${isPositive ? 'text-emerald-400' : 'text-hotpink-400'}`}>
                {isPositive ? '+' : ''}{data.change24h?.toFixed(2)}%
              </span>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        @keyframes ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-ticker {
          animation: ticker 30s linear infinite;
        }
        .animate-ticker:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}

// Combined Market Section for Landing Page
export default function MarketSection() {
  return (
    <section className="py-12 sm:py-16" data-testid="market-section">
      <div className="container-main space-y-8">
        {/* Live Ticker */}
        <LiveTicker />
        
        {/* Market Overview */}
        <LiveMarketOverview />
        
        {/* TradingView Chart */}
        <TradingViewChart defaultSymbol="BTC" />
      </div>
    </section>
  );
}
